import {
  Dispatch,
  RefObject,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { Modal, PaginationArrow } from '@/common';
import { Label, LABEL_TYPE, Spinner, ZeroState } from '@/components/generic';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import {
  AUDIO,
  COVER_PHOTO,
  IMAGE,
  PAGE_SIZE,
  STOCK_CLIP,
  TEXT,
  TRIBUTE_VIDEO,
  VIDEO
} from '@/constants';
import { AuthContext, EventContext } from '@/contexts';
import { AssetType, PERMISSION_TYPE, TributeVideoExportStatus } from '@/enums';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import { showToast, ToastType } from '@/helpers/toast';
import { AssetContainerHeader } from '@/pages/Event/Asset/AssetContainerHeader';
import { AssetDetails } from '@/pages/Event/Asset/AssetDetails';
import { AssetItem } from '@/pages/Event/Asset/AssetItem';
import { AssetTagItem } from '@/pages/Event/Asset/AssetTagItem';
import { DroppedAssetPopup } from '@/pages/Event/Asset/DroppedAssetPopup';
import { PhotoFilmIcon } from '@/resources';
import {
  useDeleteAssetMutation,
  useDeleteCoverPhotoMutation,
  useDeleteTributeVideoMutation,
  useGetEventAssets,
  useGetEventTags,
  useGetEventTributeVideos,
  useGetPublicAssets
} from '@/services';
import { useDeleteStockClipMutation, useGetStockClips } from '@/services/stockClip.service';
import {
  Asset,
  AssetsContainerProps,
  SelectOptionProps,
  Tag,
  ThemeProp,
  TributeVideoAsset
} from '@/types';
import { StockClipAsset } from '@/types/StockClipTypes';

import { TributeVideoDetails } from '../TributeVideo/CreateVideo/TributeVideoDetails';

export const AssetsContainer = ({
  eventId,
  justContent,
  isScrollable = true,
  className,
  pageSize,
  assetTag,
  assetType,
  canBeEdited,
  editSelectedAsset,
  completedVideos,
  assetContainerStyles = '',
  isPublic = false,
  sortingField,
  grow = true,
  showContentManagementAccessPopup = false,
  relatedUsersCanGenerate,
  organizationPermissions: eventOrganizationPermissions
}: AssetsContainerProps) => {
  const [search, setSearch] = useState<string>();
  const [filterByType, setFilterByType] = useState<SelectOptionProps>();
  const [selectedTags, setSelectedTags] = useState<SelectOptionProps[]>();
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [showAsset, setShowAsset] = useState<boolean>(false);
  const [assetDetailsLabel, setAssetDetailsLabel] = useState<string>();
  const [canDeleteAsset, setCanDeleteAsset] = useState<boolean>(false);
  const [showDownloadIcon, setShowDownloadIcon] = useState(false);
  const [selectedTributeVideoUrl, setSelectedTributeVideoUrl] = useState<string>();
  const [selectedTributeVideoName, setSelectedTributeVideoName] = useState<string | undefined>('');
  const { permissions, organizationPermissions: userOrganizationPermissions } =
    useContext(AuthContext);
  const organizationPermissions = eventOrganizationPermissions ?? userOrganizationPermissions;
  const [enableEdit, setEnableEdit] = useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [assetName, setAssetName] = useState<string>('');
  const { eventData } = useContext(EventContext);

  const hasOrgAccess = permissions.includes(PERMISSION_TYPE.ORG_ACCESS);

  const [dragging, setDragging] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const hasContentManagementAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
  );
  const hasTributeVideoCreation = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.TRIBUTE_VIDEO_CREATION
  );
  const [asset, setAsset] = useState<Asset | null>(null);
  const [t] = useTranslation('common');
  const { ref, inView } = useInView();

  const { data: allTags } = useGetEventTags(eventId ?? '');
  const {
    data: publicAssetsData,
    isLoading: isPublicAssetsLoading,
    fetchNextPage: publicAssetsFetchNextPage,
    isFetchingNextPage: isPublicAssetsFetchingNextPage,
    hasNextPage: publicAssetsHasNextPage
  } = assetType === STOCK_CLIP
    ? useGetStockClips(PAGE_SIZE, !eventId, search, undefined, false, sortingField)
    : useGetPublicAssets([''], PAGE_SIZE, !eventId, search, [assetType ?? ''], sortingField);

  const tributeVideoTags = allTags?.data
    ?.filter((tag: Tag) => tag.name === TRIBUTE_VIDEO)
    .map((tag: Tag) => `${tag.id}`);

  const {
    data: allTributeVideos,
    isLoading: allTributeVideosLoading,
    isFetchingNextPage: isAllTributeVideosFetchingNextPage,
    fetchNextPage: fetchAllTributeVideosNextPage,
    hasNextPage: hasAllTributeVideosNextPage
  } = useGetEventTributeVideos(
    eventId ?? '',
    PAGE_SIZE,
    hasOrgAccess ? [] : tributeVideoTags?.length === 0 ? ['-1'] : tributeVideoTags,
    undefined,
    hasTributeVideoCreation
  );

  const isSelectedAssetInTributeVideos = allTributeVideos?.pages.some((page) =>
    page.data?.items.some((video: TributeVideoAsset) => video.id === selectedAsset?.id)
  );

  const { mutate: deleteAsset } = useDeleteAssetMutation(queryClient, {
    onSuccess: () => {
      setCanDeleteAsset(false);
      showToast(ToastType.SUCCESS, t('assetSuccessfullyDeleted'));
    },
    onError: () => {
      showToast(ToastType.ERROR, t('assetDeleteError'));
    }
  });

  const { mutate: deleteCoverPhoto, isLoading: isDeletingCoverPhoto } = useDeleteCoverPhotoMutation(
    queryClient,
    {
      onSuccess: () => {
        setCanDeleteAsset(true);
      },
      onError: () => {
        showToast(ToastType.ERROR, t('assetDeleteError'));
      }
    }
  );

  const { mutate: deleteTributeVideo, isLoading: isDeletingTributeVideo } =
    useDeleteTributeVideoMutation(queryClient, {
      onSuccess: () => {
        showToast(ToastType.SUCCESS, t('assetSuccessfullyDeleted'));
      },
      onError: () => {
        showToast(ToastType.ERROR, t('assetDeleteError'));
      }
    });

  const { mutate: deleteStockClip } = useDeleteStockClipMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('assetSuccessfullyDeleted'));
    },
    onError: () => {
      showToast(ToastType.ERROR, t('assetDeleteError'));
    }
  });

  const isAssetItemDragged = (event: React.DragEvent<HTMLDivElement>) => {
    if (
      event.dataTransfer &&
      (event.dataTransfer.types.includes('application/slotjson') ||
        event.dataTransfer.types.includes('text/plain') ||
        event.dataTransfer.types.includes('text/html') ||
        event.dataTransfer.types.includes('text/uri-list'))
    ) {
      return true;
    }
    return false;
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && !isAssetItemDragged(e)) {
      setDroppedFiles(files);
      setShowPopup(true);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(!isAssetItemDragged(e));
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setDragging(false);
    }
  };

  const closeAssetModal = () => {
    setShowAsset(false);
    setEnableEdit(false);

    if (eventId) {
      refetchAssets();
    }
  };

  const deleteSelectedAsset = async (asset: Asset) => {
    setAsset(asset);
    if (justContent && asset.assetType === COVER_PHOTO) {
      setOpenConfirmDialog(true);
      setAssetName(asset?.assetName);
    } else if (justContent && assetType === AssetType.TRIBUTE_VIDEO.toString()) {
      setOpenConfirmDialog(true);
      setAssetName((asset as any)?.name);
    } else {
      if (asset.assetType === COVER_PHOTO) {
        deleteCoverPhoto(Number(asset.id));
      } else if (assetType === AssetType.TRIBUTE_VIDEO.toString()) {
        deleteTributeVideo(Number(asset.id));
      } else if (assetType === STOCK_CLIP) {
        deleteStockClip(Number(asset.id));
      } else {
        deleteAsset(asset.id);
      }
    }
  };

  const searchCallback = (query: string) => {
    setSearch(query);
  };

  const handleTagRemove = (tag: SelectOptionProps) => {
    if (selectedTags?.includes(tag)) {
      setSelectedTags(selectedTags.filter((t) => t?.value !== tag?.value));
    }
  };

  const {
    isLoading: isAllAssetsLoading,
    data: allAssets,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch: refetchAssets
  } = useGetEventAssets(
    eventId ?? '',
    assetTag?.map((tag) => tag.id.toString()) ??
      selectedTags?.map((tag: SelectOptionProps) => tag?.value as string) ??
      [],
    pageSize ?? PAGE_SIZE,
    search,
    assetType
      ? !hasOrgAccess && !relatedUsersCanGenerate
        ? Array.from(new Set([...assetType, AssetType.IMAGE.toString()]))
        : [assetType]
      : filterByType
        ? ([filterByType.value] as string[])
        : [],
    undefined,
    undefined,
    hasContentManagementAccess
  );

  useEffect(() => {
    if (
      inView &&
      (isPublic
        ? publicAssetsHasNextPage
        : assetType === AssetType.TRIBUTE_VIDEO.toString()
          ? hasAllTributeVideosNextPage
          : hasNextPage)
    ) {
      isPublic
        ? publicAssetsFetchNextPage()
        : assetType === AssetType.TRIBUTE_VIDEO.toString()
          ? fetchAllTributeVideosNextPage()
          : fetchNextPage();
    }
  }, [inView]);

  useEffect(() => {
    if (canDeleteAsset) {
      deleteAsset((asset as Asset)?.id);
      setAsset(null);
    }
  }, [canDeleteAsset]);

  useEffect(() => {
    if (selectedAsset?.status) {
      const isDownloadable = selectedAsset?.status === TributeVideoExportStatus.Completed;
      setShowDownloadIcon(isDownloadable);
    }
    if (selectedAsset?.assetType === COVER_PHOTO) {
      setShowDownloadIcon(true);
    }
    return;
  }, [selectedAsset]);

  const handleCancel = () => {
    setShowPopup(false);
    setDroppedFiles([]);
  };

  const calculateOverlayPosition = (ref: RefObject<HTMLDivElement>) => {
    if (!ref.current) {
      return {};
    }
    const rect = ref.current.getBoundingClientRect();
    return {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height
    };
  };

  const handleDeleteAsset = (assetId: number) => {
    if (assetType === AssetType.TRIBUTE_VIDEO.toString()) {
      deleteTributeVideo(assetId);
    } else if (assetType === AssetType.COVER_PHOTO.toString()) {
      deleteCoverPhoto(assetId);
    }
    setOpenConfirmDialog(false);
  };

  const isLoading = isPublic
    ? isPublicAssetsLoading
    : hasContentManagementAccess
      ? isAllAssetsLoading
      : false;

  return (
    <div
      className={`flex size-full flex-col justify-center ${isPublic ? 'mt-7 md:px-10' : 'mt-9 md:px-20'} ${className}`}>
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <Modal
            showDownloadIcon={showDownloadIcon}
            modalClasses={` max-w-full  ${selectedAsset?.assetType === IMAGE ? 'max-h-[98%] min-w-full lg:min-w-[1200px] md:max-w-[1200px]' : 'max-h-[75%] min-w-[300px] lg:min-w-[600px] md:max-w-[600px]'}`}
            height='h-full'
            isOpen={showAsset}
            label={assetDetailsLabel}
            onClose={closeAssetModal}
            downloadAssetUrl={selectedTributeVideoUrl ?? selectedAsset?.assetUrl}
            downloadAssetName={selectedTributeVideoName ?? selectedAsset?.assetName}>
            {showAsset &&
              (isSelectedAssetInTributeVideos ? (
                <TributeVideoDetails
                  id={selectedAsset?.id as number}
                  setAssetDetailsLabel={setAssetDetailsLabel}
                  setSelectedTributeVideo={setSelectedTributeVideoUrl}
                  setSelectedTributeVideoName={setSelectedTributeVideoName}
                />
              ) : (
                <AssetDetails
                  asset={selectedAsset}
                  setAssetDetailsLabel={setAssetDetailsLabel as Dispatch<SetStateAction<string>>}
                  enableEdit={enableEdit}
                  setEnableEdit={setEnableEdit}
                  closeModal={closeAssetModal}
                  allEventAssets={allAssets?.pages.flatMap((page) => page.data?.items)}
                />
              ))}
          </Modal>
          {isPublic && <Label text={t('Stock Clips')} className={'mb-8'} type={LABEL_TYPE.H1} />}
          {(isPublic
            ? publicAssetsData?.pages
            : hasContentManagementAccess
              ? allAssets?.pages
              : true) && (
            <div
              className={`${grow ? `relative grow ${assetType === STOCK_CLIP ? 'h-[calc(100vh-180px)] xl:h-64' : 'h-64'} ` : 'h-full'} ${hasContentManagementAccess && 'relative'} flex w-full flex-col justify-start`}>
              {!justContent && (
                <AssetContainerHeader
                  eventId={eventId ?? ''}
                  organizationId={eventData?.organization?.id}
                  searchCallback={searchCallback}
                  setFilterByType={setFilterByType}
                  filterByType={filterByType}
                  setSelectedTags={setSelectedTags}
                  selectedTags={selectedTags}
                  isPublic={isPublic}
                  assetType={assetType}
                  organizationPermissions={organizationPermissions}
                />
              )}
              <div
                className={`${
                  isScrollable &&
                  (allAssets?.pages[0].data?.items.length !== 0 ||
                    allTributeVideos?.pages[0].data?.items.length !== 0) &&
                  `relative flex flex-col scrollbar-none ${assetContainerStyles}`
                } ${dragging ? 'overflow-hidden' : 'overflow-y-auto'} `}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleFileDrop}
                ref={containerRef}>
                {!justContent && !isPublic && dragging && (
                  <div
                    className='pointer-events-none fixed inset-0 z-20 flex items-center justify-center border-2 border-dashed border-blue-500 bg-white/40 backdrop-blur-md'
                    style={calculateOverlayPosition(containerRef)}>
                    <Label
                      className='text-xl font-semibold'
                      type={LABEL_TYPE.H1}
                      text={t('dropHere')}
                    />
                  </div>
                )}
                {!justContent && !isPublic && showPopup && (
                  <DroppedAssetPopup
                    files={droppedFiles}
                    isPublic={false}
                    onClose={handleCancel}
                    eventId={Number(eventId)}
                    organizationId={eventData?.organization?.id}
                  />
                )}
                {selectedTags?.length ? (
                  <div className='mb-6 mt-2 flex flex-row justify-start'>
                    {selectedTags.map((tag) => (
                      <AssetTagItem
                        tag={tag}
                        handleTagRemove={handleTagRemove}
                        key={tag?.value as number}
                      />
                    ))}
                  </div>
                ) : (
                  !justContent &&
                  !isPublic && (
                    <div className='mb-6 mt-2 text-xl font-bold md:text-2xl md:text-primary-blue'>
                      {t('allUploadedContent')}
                    </div>
                  )
                )}
                {allAssets?.pages[0].data?.items.length === 0 &&
                  assetType !== AssetType.TRIBUTE_VIDEO.toString() && (
                    <div className='md:mt-20 3xl:mt-48 4xl:mt-48'>
                      <ZeroState
                        icon={<PhotoFilmIcon className='size-[30px] text-secondary-steel-blue' />}
                        titleFirstLine={t('uploadContentTitleOne')}
                        titleSecondLine={t('uploadContentTitleTwo')}
                      />
                    </div>
                  )}
                <div
                  className={`grid grid-cols-2 sm:grid-cols-3 ${
                    assetType === COVER_PHOTO
                      ? 'gap-4 lg:grid-cols-4 lg:gap-6 xl:grid-cols-4'
                      : assetType === TEXT
                        ? 'gap-4 md:grid-cols-7'
                        : 'mb-6 gap-4 lg:grid-cols-4 lg:gap-6 xl:grid-cols-5 2xl:grid-cols-5 3xl:grid-cols-5 4xl:grid-cols-5'
                  }`}>
                  {(isPublic
                    ? publicAssetsData
                    : assetType === AssetType.TRIBUTE_VIDEO.toString()
                      ? allTributeVideos
                      : allAssets
                  )?.pages
                    .flatMap((page) =>
                      assetType === STOCK_CLIP
                        ? page.data?.items.map(
                            (item: StockClipAsset) => item.stockClipAsset ?? item
                          )
                        : page.data?.items
                    )
                    .map((asset: Asset) => (
                      <AssetItem
                        setSelectedAsset={
                          setSelectedAsset as Dispatch<SetStateAction<Asset | ThemeProp | null>>
                        }
                        setShowAsset={setShowAsset}
                        setEnableEdit={setEnableEdit}
                        deleteSelectedAsset={deleteSelectedAsset}
                        asset={asset}
                        key={asset.id}
                        canBeEdited={
                          canBeEdited ||
                          asset.assetType === IMAGE ||
                          asset.assetType === VIDEO ||
                          asset.assetType === AUDIO
                        }
                        editSelectedAsset={editSelectedAsset}
                        assetType={
                          assetType === AssetType.TRIBUTE_VIDEO.toString()
                            ? assetType
                            : asset.assetType
                        }
                        showDownloadIcon={completedVideos}
                        selectedVideoUrl={''}
                      />
                    ))}
                  <div ref={ref} className='size-1'></div>
                </div>
              </div>
              {isScrollable &&
                (isPublic
                  ? !isPublicAssetsLoading
                  : assetType === AssetType.TRIBUTE_VIDEO.toString()
                    ? !allTributeVideosLoading
                    : !isLoading) && (
                  <PaginationArrow
                    isFetchingNextPage={
                      isPublic
                        ? isPublicAssetsFetchingNextPage
                        : assetType === AssetType.TRIBUTE_VIDEO.toString()
                          ? isAllTributeVideosFetchingNextPage
                          : isFetchingNextPage
                    }
                    inView={inView}
                    className='absolute bottom-0 z-30 w-full bg-white'
                  />
                )}
              {!hasContentManagementAccess && showContentManagementAccessPopup && (
                <div
                  className={`absolute inset-0 z-10 backdrop-blur-sm ${isPublic ? 'md:-mx-10' : 'md:-mx-20'}`}>
                  <Modal
                    isOpen={!hasContentManagementAccess}
                    modalClasses='md:w-[450px] w-full border border-secondary-blue-gray'
                    hasCloseIcon={false}
                    onClose={() => {
                      undefined;
                    }}>
                    <div className='flex w-full flex-col items-center justify-center gap-y-4 text-center'>
                      <Label type={LABEL_TYPE.PARAGRAPH1_BOLD} text={t('featureUnavailable')} />
                      <Label
                        type={LABEL_TYPE.PARAGRAPH2}
                        text={t('yourOranizationDoesntHavePermission', {
                          permission: t(`${ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT}`)
                        })}
                      />
                    </div>
                  </Modal>
                </div>
              )}
              <ConfirmationModal
                isOpen={openConfirmDialog}
                onClose={() => setOpenConfirmDialog(false)}
                onConfirm={() => handleDeleteAsset(Number(asset?.id))}
                message={t('deleteMessage', { item: assetName })}
                isDisabled={isDeletingTributeVideo || isDeletingCoverPhoto}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
